import { Fragment, useEffect, useMemo, useState, type CSSProperties, type FC } from "react";
import { MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import type { CloudWord, Sentiment } from "../Types/ListeningReportTypes";
import { findWordExamples, getTopWords, orderCloudWords, splitByWord } from "../Utils/ListeningReportUtils";
import { CardTitle, REPORT_KEY, ReportTag, SectionTitle } from "./ReportPrimitives";

const WORDS_KEY = `${REPORT_KEY}.words`;
const WORD_SIZE_FACTOR = 0.7;
const TIP_HALF_WIDTH = 172;
const TIP_MIN_SPACE_ABOVE = 240;
const TIP_GAP = 10;

type SentimentFilter = Sentiment | "all";
type CssVariableStyle = CSSProperties & Record<`--${string}`, string | number>;

const isSentimentFilter = (value: unknown): value is SentimentFilter =>
  value === "all" || value === "pos" || value === "neu" || value === "neg";

const getWordStyle = (weight: number): CssVariableStyle => ({
  "--br-s": Math.round(weight * WORD_SIZE_FACTOR),
});

const SENTIMENT_TAG_TONE: Record<Sentiment, "pos" | "neg" | undefined> = {
  pos: "pos",
  neg: "neg",
  neu: undefined,
};

const SENTIMENT_COLOR: Record<Sentiment, string> = {
  pos: "var(--br-pos-bar)",
  neg: "var(--br-neg-bar)",
  neu: "var(--br-neu)",
};

interface ActiveWord {
  word: string;
  sentiment: Sentiment;
  rect: DOMRect;
}

const WordTooltip: FC<{ active: ActiveWord; comments: string[] }> = ({ active, comments }) => {
  const { t } = useTranslation();
  const examples = findWordExamples(active.word, comments);
  const placeAbove = active.rect.top >= TIP_MIN_SPACE_ABOVE;
  const centerX = Math.min(
    Math.max(active.rect.left + active.rect.width / 2, TIP_HALF_WIDTH),
    window.innerWidth - TIP_HALF_WIDTH
  );
  const style: CssVariableStyle = {
    left: centerX,
    top: placeAbove ? active.rect.top - TIP_GAP : active.rect.bottom + TIP_GAP,
    transform: placeAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
    "--br-tip-color": SENTIMENT_COLOR[active.sentiment],
  };

  const sentimentLabels: Record<Sentiment, string> = {
    pos: t(`${WORDS_KEY}.positive`, "Positive"),
    neu: t(`${WORDS_KEY}.neutral`, "Neutral"),
    neg: t(`${WORDS_KEY}.negative`, "Negative"),
  };
  const reasons: Record<Sentiment, string> = {
    pos: t(`${WORDS_KEY}.reasonPositive`, "Appears mostly in positive comments"),
    neu: t(`${WORDS_KEY}.reasonNeutral`, "Appears about equally in positive and negative comments"),
    neg: t(`${WORDS_KEY}.reasonNegative`, "Appears mostly in negative comments"),
  };

  return (
    <div className="br__wtip" role="tooltip" id="br-word-tip" style={style}>
      <div className="br__wt-top">
        <span className="br__wt-word">{active.word}</span>
        <ReportTag small tone={SENTIMENT_TAG_TONE[active.sentiment]}>
          {sentimentLabels[active.sentiment]}
        </ReportTag>
      </div>
      <div className="br__wt-why">{reasons[active.sentiment]}.</div>
      <div className="br__wt-label">{t(`${WORDS_KEY}.examples`, "Example comments")}</div>
      {examples.length ? (
        <ul>
          {examples.map(example => (
            <li key={example}>
              “
              {splitByWord(example, active.word).map((part, index) =>
                index % 2 ? <mark key={index}>{part}</mark> : <Fragment key={index}>{part}</Fragment>
              )}
              ”
            </li>
          ))}
        </ul>
      ) : (
        <p className="br__wt-none">
          {t(`${WORDS_KEY}.noExamples`, "None of the comments shown in this report use this word.")}
        </p>
      )}
    </div>
  );
};

const FrequencyList: FC<{ title: string; words: CloudWord[]; tone: "pos" | "neg" }> = ({ title, words, tone }) => {
  const maxWeight = words[0]?.weight ?? 1;
  return (
    <div className="br__freq">
      <h3>{title}</h3>
      {words.map(word => (
        <div className="br__frow" key={word.word}>
          <span>{word.word}</span>
          <div className="br__frow-track">
            <div
              className={`br__frow-fill br__frow-fill--${tone}`}
              style={{ width: `${(word.weight / maxWeight) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

interface WordCloudSectionProps {
  words: CloudWord[];
  comments: string[];
  primaryName: string;
}

const WordCloudSection: FC<WordCloudSectionProps> = ({ words, comments, primaryName }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<SentimentFilter>("all");
  const [active, setActive] = useState<ActiveWord | null>(null);
  const orderedWords = useMemo(() => orderCloudWords(words), [words]);
  const filterOptions = useMemo(
    () => [
      { value: "all", label: t(`${WORDS_KEY}.filterAll`, "All sentiments") },
      { value: "pos", label: t(`${WORDS_KEY}.positive`, "Positive") },
      { value: "neu", label: t(`${WORDS_KEY}.neutral`, "Neutral") },
      { value: "neg", label: t(`${WORDS_KEY}.negative`, "Negative") },
    ],
    [t]
  );

  useEffect(() => {
    if (!active) {
      return;
    }
    const hide = () => setActive(null);
    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hide();
      }
    };
    window.addEventListener("scroll", hide, true);
    window.addEventListener("keydown", hideOnEscape);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("keydown", hideOnEscape);
    };
  }, [active]);

  const showWord = (word: CloudWord, element: HTMLElement) =>
    setActive({ word: word.word, sentiment: word.sentiment, rect: element.getBoundingClientRect() });

  return (
    <section className="br__panel" id="br-words" data-pdf-block="br-words" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.words`, "Word Cloud")}>
      <SectionTitle
        scope={{ label: t(`${REPORT_KEY}.scope.only`, "{{primary}} only", { primary: primaryName }) }}
      >
        {t(`${REPORT_KEY}.tabs.words`, "Word Cloud")}
      </SectionTitle>
      <div className="br__card">
        <div className="br__card-top">
          <div>
            <CardTitle
              help={t(
                `${WORDS_KEY}.help`,
                "Size shows frequency; colour shows the sentiment of comments containing the word"
              )}
            >
              {t(`${WORDS_KEY}.title`, "Most Used Words in {{primary}} Comments", { primary: primaryName })}
            </CardTitle>
            <p className="br__card-desc">
              {t(`${WORDS_KEY}.description`, "Mentions, URLs and generic filler words removed.")}
            </p>
            <p className="br__hover-hint">
              <MessageSquare className="br-ic" aria-hidden />
              {t(
                `${WORDS_KEY}.hint`,
                "Hover over any word (or tap it on mobile) to see its sentiment and example comments that use it."
              )}
            </p>
          </div>
          <div className="br__dd">
            <StyledDropdown
              value={filter}
              options={filterOptions}
              onChange={value => isSentimentFilter(value) && setFilter(value)}
              aria-label={t(`${WORDS_KEY}.filterLabel`, "Highlight words by sentiment")}
            />
          </div>
        </div>
        <div className="br__note-box br__note-box--how">
          <b>{t(`${WORDS_KEY}.howLead`, "How the colours work:")}</b>{" "}
          {t(
            `${WORDS_KEY}.howBody`,
            "a word isn’t judged as good or bad on its own. Each word takes the colour of the comments it appears in most often. Green words mostly show up in compliments, red words mostly show up in complaints, and grey words appear about equally in positive and negative comments."
          )}
        </div>
        <div className="br__cloud-layout">
          <div className="br__cloud">
            {orderedWords.map(word => {
              const isDimmed = filter !== "all" && word.sentiment !== filter;
              return (
                <button
                  key={word.word}
                  type="button"
                  className={`br__word br__word--${word.sentiment}${isDimmed ? " br__word--dim" : ""}${
                    active?.word === word.word ? " br__word--active" : ""
                  }`}
                  style={getWordStyle(word.weight)}
                  aria-describedby={active?.word === word.word ? "br-word-tip" : undefined}
                  onMouseEnter={event => showWord(word, event.currentTarget)}
                  onFocus={event => showWord(word, event.currentTarget)}
                  onMouseLeave={() => setActive(null)}
                  onBlur={() => setActive(null)}
                  onClick={event =>
                    active?.word === word.word ? setActive(null) : showWord(word, event.currentTarget)
                  }
                >
                  {word.word}
                </button>
              );
            })}
          </div>
          <div>
            <FrequencyList
              title={t(`${WORDS_KEY}.topPositive`, "Words seen most in positive comments")}
              words={getTopWords(words, "pos")}
              tone="pos"
            />
            <FrequencyList
              title={t(`${WORDS_KEY}.topNegative`, "Words seen most in negative comments")}
              words={getTopWords(words, "neg")}
              tone="neg"
            />
          </div>
        </div>
      </div>
      {active && <WordTooltip active={active} comments={comments} />}
    </section>
  );
};

export default WordCloudSection;
